import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import koneLogo from '../assets/kone-logo.png';
import insightMinerLogo from '../assets/insightMiner.png';
import releaseData from '../config/release.json';
import useUserPermissions from '../hooks/useUserPermissions';

function Header({ signOut, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAboutModalOpen, setIsAboutModalOpen] = React.useState(false);
  const {
    canAccessIncidentResolver,
    canAccessSnAnalysis,
    canAccessProdPerformance,
    canAccessAiGovernance,
    canAccessRunbooks,
    isLoading,
  } = useUserPermissions();

  // Extract first name from loginId/username (synchronous, always available)
  const getNameFromLoginId = React.useCallback(() => {
    const raw = (user?.signInDetails?.loginId || user?.username || '').replace('KONE-AzureAD_', '');
    const namePart = raw.split('@')[0].split('.')[0];
    return namePart ? namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase() : '';
  }, [user]);

  // Immediate fallback so duplicated tabs always show something
  const [firstName, setFirstName] = React.useState(() => '');

  React.useEffect(() => {
    if (!user) return;

    // Set the loginId-derived name immediately (works even on duplicated tabs)
    setFirstName(getNameFromLoginId());

    // Then try to upgrade with the proper claim from the ID token
    let cancelled = false;
    fetchAuthSession()
      .then(({ tokens }) => {
        if (cancelled) return;
        const idToken = tokens?.idToken;
        if (!idToken) return;
        const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
        if (payload.given_name) {
          setFirstName(payload.given_name);
        } else if (payload.name) {
          setFirstName(payload.name.split(' ')[0]);
        }
      })
      .catch(() => {
        // Token unavailable — loginId-based name is already set, nothing to do
      });

    return () => {
      cancelled = true;
    };
  }, [user, getNameFromLoginId]);

  // Check active state for each page
  const isIncidentActive =
    location.pathname.includes('/incident_view') ||
    location.pathname.includes('/devops/sessions') ||
    location.pathname === '/';
  const isSnAnalysisActive = location.pathname.includes('/sn-analysis');
  const isProdPerfActive = location.pathname.includes('/prod-performance');
  const isAiGovernanceActive = location.pathname.includes('/genai-evals');
  const isRunbookActive =
    location.pathname.includes('/runbook') || location.pathname.includes('/edit-runbook');

  return (
    <>
      <header className="kone-header">
        <div className="kone-header-left">
          <a href="/" className="kone-logo-link">
            <img src={koneLogo} alt="KONE Logo" className="kone-logo-img" />
          </a>
          <div className="kone-brand">
            <img src={insightMinerLogo} alt="Insight Miner" className="kone-resolveguru-logo" />
            <span className="kone-brand-name">Insight Miner</span>
          </div>
          <div className="kone-header-divider" />
          <nav className="kone-nav">
            {!isLoading && canAccessAiGovernance() && (
              <button
                id="aiGovernance"
                onClick={() => navigate('/genai-evals')}
                className={`kone-nav-tab ${isAiGovernanceActive ? 'kone-nav-tab-active' : ''}`}
              >
                AI Governance
              </button>
            )}
            {!isLoading && canAccessProdPerformance() && (
              <button
                id="prodProductPerformance"
                onClick={() => navigate('/prod-performance')}
                className={`kone-nav-tab ${isProdPerfActive ? 'kone-nav-tab-active' : ''}`}
              >
                Prod Product Performance
              </button>
            )}
            {!isLoading && canAccessSnAnalysis() && (
              <button
                id="snAnalysis"
                onClick={() => navigate('/sn-analysis')}
                className={`kone-nav-tab ${isSnAnalysisActive ? 'kone-nav-tab-active' : ''}`}
              >
                Service Need Analysis
              </button>
            )}
            {!isLoading && canAccessIncidentResolver() && (
              <button
                id="incidentid"
                onClick={() => navigate('/incident_view')}
                className={`kone-nav-tab ${isIncidentActive ? 'kone-nav-tab-active' : ''}`}
              >
                ServiceNow Resolver
              </button>
            )}
            {!isLoading && canAccessRunbooks() && (
              <button
                id="Runbooks"
                onClick={() => navigate('/runbook_list')}
                className={`kone-nav-tab ${isRunbookActive ? 'kone-nav-tab-active' : ''}`}
              >
                Runbooks
              </button>
            )}
          </nav>
        </div>

        <div className="kone-header-right">
          {firstName && <span className="kone-header-welcome">Welcome, {firstName}</span>}
          <button
            onClick={() => setIsAboutModalOpen(true)}
            className="kone-header-icon-btn"
            title="About"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3" strokeLinecap="round" />
              <line x1="12" y1="12" x2="12" y2="16" />
            </svg>
          </button>
          <button onClick={signOut} className="kone-header-action">
            Logout
          </button>
        </div>
      </header>

      {/* About / Release Notes Modal */}
      {isAboutModalOpen && (
        <div className="about-modal-overlay" onClick={() => setIsAboutModalOpen(false)}>
          <div className="about-modal" onClick={(e) => e.stopPropagation()}>
            <div className="about-modal-header">
              <h3>About</h3>
              <button className="about-modal-close-icon" onClick={() => setIsAboutModalOpen(false)}>
                &times;
              </button>
            </div>
            <div className="about-modal-body">
              {releaseData?.map((release) => (
                <div key={release.version} className="about-modal-release">
                  <strong>Version {release.version}</strong>
                  {release.date && <span className="about-modal-date"> — {release.date}</span>}
                  {release.description.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              ))}
            </div>
            {/* <div className="about-modal-footer">
              <button onClick={() => setIsAboutModalOpen(false)} className="kone-btn kone-btn-secondary">Close</button>
            </div> */}
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
